// The /map URL contract. Every view of the map is addressable —
//   /map?state=texas&county=travis&layer=season&z=6
// — so a view can be linked, shared, bookmarked, and rendered as an OG image.
//
// Validation is total: a malformed or hostile query string can never throw a route
// error, it just degrades toward the national default. That matters because these
// URLs get shared, truncated by chat clients, and hand-edited.
//
// Deliberately data-free: this module resolves the *shape* of a slug, not whether it
// names a real place. Resolving "texas" to a state happens where the data already
// is, which keeps the route's eager chunk from pulling in states.ts.
//
// Hand-written rather than zod: this runs in the /map route's validateSearch, which is in
// the entry chunk every page downloads, and zod added about 48KB to it.

export const MAP_LAYERS = ["awareness", "season", "species"] as const;
export type MapLayer = (typeof MAP_LAYERS)[number];
export const DEFAULT_LAYER: MapLayer = "awareness";

/** Matches the slugs scripts/geo-shared.mjs emits, and nothing else. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_SLUG_LENGTH = 60;

const MIN_ZOOM = 1;
const MAX_ZOOM = 32;

/** A slug, or undefined for anything that isn't one. */
function slugOf(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim().toLowerCase();
  return s.length <= MAX_SLUG_LENGTH && SLUG.test(s) ? s : undefined;
}

/** A zoom within range, read from a number or a numeric string; otherwise undefined. */
function zoomOf(v: unknown): number | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n >= MIN_ZOOM && n <= MAX_ZOOM ? n : undefined;
}

function layerOf(v: unknown): MapLayer {
  return (MAP_LAYERS as readonly unknown[]).includes(v) ? (v as MapLayer) : DEFAULT_LAYER;
}

export interface MapSearch {
  /** State slug, e.g. "texas". Undefined means the national view. */
  state?: string;
  /** County slug within `state`. Ignored without a state. */
  county?: string;
  layer: MapLayer;
  /** Explicit zoom. Undefined means "frame whatever is selected". */
  z?: number;
}

/**
 * Parse an unknown query object into a MapSearch. Never throws: unknown keys are
 * dropped, bad values fall back to the default, and a county without a state is
 * discarded rather than left dangling (it can't be resolved without one).
 */
export function validateMapSearch(input: unknown): MapSearch {
  const q =
    input && typeof input === "object" && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : {};
  const layer = layerOf(q.layer);
  const z = zoomOf(q.z);
  const state = slugOf(q.state);
  if (!state) return { layer, z };
  return { state, county: slugOf(q.county), layer, z };
}

/**
 * The inverse: build the smallest query object that reproduces a view. Defaults are
 * omitted so the common case is a clean `/map`, and `z` is dropped when it matches
 * the framing a selection would produce anyway.
 */
export function toSearchParams(search: MapSearch): Partial<MapSearch> {
  const out: Partial<MapSearch> = {};
  if (search.state) out.state = search.state;
  if (search.state && search.county) out.county = search.county;
  if (search.layer !== DEFAULT_LAYER) out.layer = search.layer;
  if (typeof search.z === "number" && Number.isFinite(search.z)) {
    out.z = Math.round(search.z * 100) / 100;
  }
  return out;
}

/** Human-readable label for a layer, used by the switcher, the legend and OG text. */
export const layerLabel: Record<MapLayer, string> = {
  awareness: "Where they live",
  season: "Peak season",
  species: "Tick species reported",
};

/** One sentence saying what the layer shows — sits under the switcher, inline. */
export const layerDescription: Record<MapLayer, string> = {
  awareness: "How widely Lone Star ticks are reported in each state.",
  season: "When Lone Star tick activity typically peaks.",
  species: "How many of the tick species we profile are reported in each state.",
};
